import { Novel } from '@/types';
import { Link } from 'react-router-dom';
import { Badge } from './ui/badge';
import { Card, CardContent } from './ui/card';
import { Skeleton } from './ui/skeleton';

interface NovelCardProps {
  novel: Novel;
}

const statusColors: Record<string, 'default' | 'secondary' | 'destructive' | 'outline' | 'scp'> = {
  ongoing: 'scp',
  completed: 'secondary',
  hiatus: 'outline',
};

export function NovelCard({ novel }: NovelCardProps) {
  const statusLabels = {
    ongoing: '연재중',
    completed: '완결',
    hiatus: '휴재',
  };

  return (
    <Link to={`/novels/${novel.id}`}>
      <Card className="group hover:shadow-lg transition-all duration-300 overflow-hidden">
        <div className="aspect-[3/4] relative bg-muted overflow-hidden">
          {novel.coverUrl ? (
            <img
              src={novel.coverUrl}
              alt={novel.title}
              loading="lazy"
              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-slate-200 to-slate-300 dark:from-slate-700 dark:to-slate-800">
              <span className="text-4xl font-bold text-slate-400 dark:text-slate-600">
                {novel.scpCode}
              </span>
            </div>
          )}
          <div className="absolute top-2 left-2 flex flex-col gap-1">
            <Badge variant={statusColors[novel.status]}>
              {statusLabels[novel.status]}
            </Badge>
            {novel.tier === 'aspirant' && (
              <Badge className="bg-amber-500 hover:bg-amber-500 text-white text-[10px]">
                지망생
              </Badge>
            )}
          </div>
        </div>
        <CardContent className="p-4">
          <h3 className="font-semibold text-lg mb-1 line-clamp-1 group-hover:text-primary transition-colors">
            {novel.title}
          </h3>
          <p className="text-sm text-muted-foreground mb-2 line-clamp-2">
            {novel.summary}
          </p>
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>{novel.authorName}</span>
            <div className="flex items-center gap-2">
              <span>조회수 {novel.views.toLocaleString()}</span>
              <span>♥ {novel.likes.toLocaleString()}</span>
            </div>
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

interface NovelCardSkeletonProps {
  count?: number;
}

export function NovelCardSkeleton({ count = 6 }: NovelCardSkeletonProps) {
  return (
    <>
      {Array.from({ length: count }).map((_, i) => (
        <Card key={i} className="overflow-hidden">
          <Skeleton className="aspect-[3/4] w-full" />
          <CardContent className="p-4 space-y-2">
            <Skeleton className="h-5 w-3/4" />
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-2/3" />
            <div className="flex justify-between pt-2">
              <Skeleton className="h-3 w-20" />
              <Skeleton className="h-3 w-24" />
            </div>
          </CardContent>
        </Card>
      ))}
    </>
  );
}
